import React, { useState } from "react"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  OrderModificationShippingFees,
  Reason
} from "lib/types/generated/graphql-types"
import { Container } from "../../../../../views/Orders/ManageOrder/ManageOrder.styled"
import { OrderSummary } from "../ModifyShippingFees/OrderSummary/OrderSummary"
import { Cart } from "../ModifyShippingFees/Cart/Cart"
import { ModificationShippingFees } from "../../../../../classes/order/ModificationShippingFees"
import AddOrUpdateShippingFeeSideBar from "./EditShippingFee/UpdateShippingFeeSideBar"
import StatusPopup from "./Cart/StatusPopup"
import ModificationAction from "./Cart/ModificationAction"
import PendingShippingFeeModification from "./OrderSummary/PendingShippingFeeModification"
import FailedShippingFeeModifications from "./FailedShippingFeeModifications"
import { ReasonType } from "lib/types/order"

type Props = {
  order: graphql.Order
  refetch: () => void
  loading: boolean
  orderHistory: graphql.OrderHistory
  customReasons: CustomReasonCode[]
}

const ModifyOrderLines = ({ order, refetch, loading, orderHistory, customReasons }: Props) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [startedModification, setStartedModification] = useState("")
  const [editShippingFee, setEditShippingFee] = useState<graphql.ShippingFee | undefined>()
  const [paymentAction, setPaymentAction] =
    useState<graphql.InputActionPaymentOrderModificationOrderLines>({
      actionType: graphql.InputActionType.Auto
    })
  const [reason, setReason] = useState<Reason>({cause: "", code: ""})
  const [reasonType, setReasonType] = useState<ReasonType | undefined>()

  const modificationShippingFees = new ModificationShippingFees(
    order.Modifications as OrderModificationShippingFees[]
  )

  if (modificationShippingFees.pending() && !showStatusPopup)
    return (
      <PendingShippingFeeModification
        pendingModification={modificationShippingFees.getPendingModification()}
        orderRefetch={refetch}
        orderLoading={loading}
      />
    )

  return (
    <>
      {showStatusPopup && startedModification && (
        <StatusPopup
          setShowStatusPopup={setShowStatusPopup}
          modificationId={startedModification}
          refetchOrder={refetch}
        />
      )}
      {modificationShippingFees.failed() ? (
        modificationShippingFees
          .failedModifications()
          .map((modification: graphql.OrderModification) => (
            <FailedShippingFeeModifications
              key={modification.id}
              failedModification={modification}
              currencyCode={order.currencyCode}
              setShowStatusPopup={setShowStatusPopup}
              setStartedModification={setStartedModification}
              orderHistory={orderHistory.history}
              refetch={refetch}
            />
          ))
      ) : !showConfirmation && order ? (
        <Container>
          <AddOrUpdateShippingFeeSideBar
            order={order}
            refetch={refetch}
            editShippingFee={editShippingFee}
            setEditShippingFee={setEditShippingFee}
            reason={reason}
          />
          <div>
            <OrderSummary
              order={order}
              refetch={refetch}
              orderLoading={loading}
              editShippingFee={editShippingFee}
              setEditShippingFee={setEditShippingFee}
              reason={reason}
              setReason={setReason}
              reasonType={reasonType}
              setReasonType={setReasonType}
              customReasons={customReasons}
            />
          </div>
          <div>
            <Cart
              order={order}
              refetch={refetch}
              setShowConfirmation={setShowConfirmation}
              orderLoading={loading}
            />
          </div>
        </Container>
      ) : (
        <Container>
          <div>
            <Cart
              order={order}
              refetch={refetch}
              setShowConfirmation={setShowConfirmation}
              summaryMode={true}
              orderLoading={loading}
            />
          </div>
          <div>
            {modificationShippingFees
              .onGoingModifications()
              ?.map((modification) => (
                <ModificationAction
                  key={modification.id}
                  modification={modification}
                  paymentAction={paymentAction}
                  setPaymentAction={setPaymentAction}
                  orderId={order.id}
                  setShowStatusPopup={setShowStatusPopup}
                  setStartedModification={setStartedModification}
                  setShowConfirmation={setShowConfirmation}
                />
              ))}
          </div>
        </Container>
      )}
    </>
  )
}

export default ModifyOrderLines
